import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, MapPin, ShoppingBag, Plus, Minus, Trash2, Bike, Clock, CreditCard, Loader2, UtensilsCrossed,
} from "lucide-react";
import api from "../lib/api";

// Parse "11:30-14:00, 18:30-22:00" → ["11:30","12:00","12:30","13:00","13:30","18:30",...,"21:30"]
// Supporte séparateurs : virgule, point-virgule, retour ligne. Formats HH:MM ou HHhMM. Tirets - – ou "à".
function parseOpeningHoursToSlots(text) {
  if (!text || typeof text !== "string") return [];
  const out = [];
  const rx = /(\d{1,2})[:h](\d{2})\s*[-–à]\s*(\d{1,2})[:h](\d{2})/gi;
  let m;
  while ((m = rx.exec(text)) !== null) {
    const start = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    const end   = parseInt(m[3], 10) * 60 + parseInt(m[4], 10);
    if (isNaN(start) || isNaN(end) || end <= start) continue;
    for (let cur = start; cur < end; cur += 30) {
      const hh = String(Math.floor(cur / 60)).padStart(2, "0");
      const mn = String(cur % 60).padStart(2, "0");
      out.push(`${hh}:${mn}`);
    }
  }
  return Array.from(new Set(out)).sort();
}

// Formatte un Date en "YYYY-MM-DDTHH:MM" LOCAL (pour <input datetime-local>)
const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
// Formatte un Date en "YYYY-MM-DD" LOCAL
const toLocalDateValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { AddressAutocomplete } from "../components/AddressAutocomplete";

const BASE_FEE = 2.50; // repli si le calcul dynamique échoue

/**
 * Page de commande food dédiée — 100 % food, ZÉRO élément VTC.
 * Pas de types de véhicule, pas de choix de mode, pas de destination "course".
 * Uniquement : panier + adresse de livraison + total + paiement.
 */
export default function FoodCheckout() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feeQuote, setFeeQuote] = useState({ fee_eur: BASE_FEE, distance_km: null, computed: false, quoting: false });
  // Livraison planifiée (mode "Plus tard")
  const [scheduleMode, setScheduleMode] = useState("now");  // "now" | "later"
  const [scheduleDate, setScheduleDate] = useState("");     // "YYYY-MM-DD"
  const [scheduleTime, setScheduleTime] = useState("");     // "HH:MM" choisi parmi les créneaux resto
  const [restoSlots, setRestoSlots] = useState([]);         // Créneaux HH:MM parsés depuis resto.delivery_slots_text

  useEffect(() => {
    try {
      const raw = localStorage.getItem("nold_active_cart");
      if (raw) {
        const c = JSON.parse(raw);
        setCart(c);
        if (c.scheduled_for) {
          setScheduleMode("later");
          const d = new Date(c.scheduled_for);
          setScheduleDate(d.toISOString().slice(0, 10));
          setScheduleTime(d.toTimeString().slice(0, 5));
        } else if (c.schedule_pref === "later") {
          // Pré-sélection depuis la page restaurant (toggle "Plus tard")
          setScheduleMode("later");
        }
      }
    } catch { /* ignore */ }
  }, []);

  // Charge les créneaux du resto (delivery_slots_text) une fois le cart chargé
  useEffect(() => {
    if (!cart?.restaurant_id) return;
    (async () => {
      try {
        const { data } = await api.get(`/restaurants/${cart.restaurant_id}`);
        // Priorité 1 : générer depuis opening_hours_text (créneaux 30 min auto)
        const opening = data?.opening_hours_text || "";
        let slots = parseOpeningHoursToSlots(opening);
        // Fallback : créneaux fixes explicites dans delivery_slots_text
        if (slots.length === 0) {
          const raw = data?.delivery_slots_text || "";
          slots = raw.split(/[,;\n]/).map((s) => s.trim()).filter((s) => /^\d{1,2}:\d{2}$/.test(s));
        }
        setRestoSlots(slots);
      } catch { setRestoSlots([]); }
    })();
  }, [cart?.restaurant_id]);

  const items = cart?.items || [];
  const subtotal = useMemo(() => items.reduce((s, x) => s + (x.qty * x.price_eur), 0), [items]);
  const activeFee = items.length ? feeQuote.fee_eur : 0;
  const total = subtotal + activeFee;

  // Debounced quote : refetch les frais dynamiques dès que l'adresse ou le resto change.
  useEffect(() => {
    if (!cart?.restaurant_id || address.trim().length < 6) {
      setFeeQuote({ fee_eur: BASE_FEE, distance_km: null, computed: false, quoting: false });
      return;
    }
    const t = setTimeout(async () => {
      setFeeQuote((q) => ({ ...q, quoting: true }));
      try {
        const { data } = await api.get("/food/delivery-fee-quote", {
          params: { restaurant_id: cart.restaurant_id, address },
        });
        setFeeQuote({
          fee_eur: Number(data.fee_eur) || BASE_FEE,
          distance_km: data.distance_km,
          computed: !!data.computed,
          within_radius: data.within_radius !== false,
          max_radius_km: data.max_radius_km,
          quoting: false,
        });
      } catch {
        setFeeQuote({ fee_eur: BASE_FEE, distance_km: null, computed: false, quoting: false });
      }
    }, 600);
    return () => clearTimeout(t);
  }, [cart?.restaurant_id, address]);

  const updateQty = (id, delta) => {
    const next = { ...cart, items: cart.items.map(x => x.id === id ? { ...x, qty: Math.max(0, x.qty + delta) } : x).filter(x => x.qty > 0) };
    setCart(next);
    try { localStorage.setItem("nold_active_cart", JSON.stringify(next)); } catch { /* ignore */ }
  };

  const clearCart = () => {
    if (!window.confirm("Vider le panier ?")) return;
    localStorage.removeItem("nold_active_cart");
    setCart(null);
  };

  const scheduledForIso = useMemo(() => {
    if (scheduleMode !== "later" || !scheduleDate || !scheduleTime) return null;
    try {
      const local = new Date(`${scheduleDate}T${scheduleTime}:00`);
      if (isNaN(local.getTime())) return null;
      return local.toISOString();
    } catch { return null; }
  }, [scheduleMode, scheduleDate, scheduleTime]);

  const scheduleValid = scheduleMode === "now" || !!scheduledForIso;
  const canPay = items.length > 0 && address.trim().length > 5 && firstName.trim().length >= 2 && email.includes("@") && phone.trim().length >= 8 && scheduleValid;

  const pay = async () => {
    if (!canPay) return;
    setSubmitting(true);
    try {
      // Créé la commande + génère le lien Stripe Checkout (l'inscription se fait automatiquement au paiement)
      const r = await api.post("/food/checkout", {
        restaurant_id: cart.restaurant_id,
        restaurant_name: cart.restaurant_name,
        items: items.map(x => ({ id: x.id, name: x.name, qty: x.qty, price_eur: x.price_eur })),
        delivery_address: address,
        notes,
        contact: { first_name: firstName, phone, email },
        scheduled_for: scheduledForIso,   // ISO datetime UTC ou null (immédiat)
      });
      const url = r.data?.checkout_url;
      if (url) {
        localStorage.removeItem("nold_active_cart");
        window.location.href = url;
      } else {
        toast.error("Paiement indisponible pour le moment.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la commande.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!cart || items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <ShoppingBag className="w-16 h-16 text-[#75807A] mx-auto mb-4 opacity-50" />
          <h1 className="font-head font-black text-2xl text-[#1A1A1A]">Votre panier est vide</h1>
          <p className="text-sm text-[#75807A] mt-2">Choisissez un restaurant et ajoutez des plats pour commander.</p>
          <Link
            to="/"
            data-testid="checkout-empty-home"
            className="mt-6 inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold transition-colors"
          >
            <UtensilsCrossed className="w-4 h-4 mr-2" /> Découvrir les restaurants
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0] pb-40">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E2DFD8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <button
            data-testid="checkout-back"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full hover:bg-[#F0EBE1] flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-head font-black text-lg">Votre commande</h1>
            <p className="text-xs text-[#75807A]">{cart.restaurant_name}</p>
          </div>
          <button
            data-testid="checkout-clear"
            onClick={clearCart}
            className="text-xs text-[#DC2626] hover:underline font-bold"
          >
            Vider
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Panier */}
        <section data-testid="checkout-cart" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
          <h2 className="font-head font-bold text-[#1A1A1A] flex items-center gap-2 mb-4">
            <ShoppingBag className="w-5 h-5 text-[#059669]" /> Panier ({items.length})
          </h2>
          <div className="space-y-3">
            {items.map(it => (
              <div key={it.id} data-testid={`cart-item-${it.id}`} className="flex items-center gap-3 py-2 border-b border-[#F0EBE1] last:border-0">
                <div className="flex-1">
                  <div className="font-bold text-sm text-[#1A1A1A]">{it.name}</div>
                  <div className="text-xs text-[#75807A]">{it.price_eur.toFixed(2)} €</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`cart-minus-${it.id}`}
                    onClick={() => updateQty(it.id, -1)}
                    className="w-8 h-8 rounded-full bg-[#F0FDF4] hover:bg-[#059669] hover:text-white text-[#059669] flex items-center justify-center transition-colors"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-6 text-center font-bold">{it.qty}</span>
                  <button
                    data-testid={`cart-plus-${it.id}`}
                    onClick={() => updateQty(it.id, 1)}
                    className="w-8 h-8 rounded-full bg-[#F0FDF4] hover:bg-[#059669] hover:text-white text-[#059669] flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="w-16 text-right font-bold text-[#1A1A1A]">{(it.qty * it.price_eur).toFixed(2)} €</div>
              </div>
            ))}
          </div>
        </section>

        {/* Livraison */}
        <section data-testid="checkout-delivery" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
          <h2 className="font-head font-bold text-[#1A1A1A] flex items-center gap-2 mb-4">
            <Bike className="w-5 h-5 text-[#059669]" /> Adresse de livraison
          </h2>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-[#3B423E] mb-1 block">Adresse complète *</label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={(r) => setAddress(r?.display_name || r?.formatted || address)}
                placeholder="Rue de la Loi 16, 1000 Bruxelles"
                country="be"
                testid="checkout-address"
                dotColor="#059669"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#3B423E] mb-1 block">Instructions livreur (optionnel)</label>
              <Input
                data-testid="checkout-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Étage, code d'entrée, préférences…"
                className="h-12 rounded-xl border-[#E2DFD8] focus:border-[#059669]"
              />
            </div>
            <div className="inline-flex items-center gap-2 text-xs text-[#059669] font-bold">
              <Clock className="w-4 h-4" /> Livraison estimée dans 30 minutes
            </div>
          </div>
        </section>

        {/* Livraison planifiée — toggle "Immédiat" / "Plus tard" + sélecteur date/heure */}
        <section data-testid="checkout-schedule" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
          <h2 className="font-head font-bold text-[#1A1A1A] flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-[#059669]" /> Quand livrer ?
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="schedule-now"
              onClick={() => setScheduleMode("now")}
              className={`h-11 rounded-xl border-2 font-semibold text-sm transition-colors ${scheduleMode === "now" ? "border-[#059669] bg-[#F0FDF4] text-[#065F46]" : "border-[#E2DFD8] hover:border-[#059669]"}`}>
              🛵 Immédiat (30 min)
            </button>
            <button type="button" data-testid="schedule-later"
              onClick={() => setScheduleMode("later")}
              className={`h-11 rounded-xl border-2 font-semibold text-sm transition-colors ${scheduleMode === "later" ? "border-[#F26419] bg-[#FFF3E6] text-[#7C2D12]" : "border-[#E2DFD8] hover:border-[#F26419]"}`}>
              📅 Planifier
            </button>
          </div>
          {scheduleMode === "later" && (
            <div className="mt-4 space-y-3">
              {restoSlots.length > 0 ? (
                // Mode 1 : Calendrier (demain → +7j) + créneaux 30 min générés depuis opening_hours_text
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-[#3B423E] mb-1 block">Date</label>
                    <input type="date" data-testid="schedule-date" value={scheduleDate}
                      min={toLocalDateValue(new Date(Date.now() + 24 * 3600 * 1000))}
                      max={toLocalDateValue(new Date(Date.now() + 7 * 24 * 3600 * 1000))}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="w-full h-12 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none px-3 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[#3B423E] mb-1 block">Créneau horaire</label>
                    <select data-testid="schedule-time" value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="w-full h-12 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none px-3 text-sm bg-white">
                      <option value="">Choisir…</option>
                      {restoSlots.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                </div>
              ) : (
                // Mode 2 : opening_hours vide/inexploitable → input libre
                <div>
                  <label className="text-xs font-bold text-[#3B423E] mb-1 block">Date et heure de livraison</label>
                  <input type="datetime-local" data-testid="schedule-datetime-free"
                    min={toLocalInputValue(new Date(Date.now() + 30 * 60 * 1000))}
                    max={toLocalInputValue(new Date(Date.now() + 7 * 24 * 3600 * 1000))}
                    value={scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}` : ""}
                    onChange={(e) => {
                      const v = e.target.value; // "YYYY-MM-DDTHH:MM"
                      const [d, t] = (v || "").split("T");
                      setScheduleDate(d || "");
                      setScheduleTime(t || "");
                    }}
                    className="w-full h-12 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none px-3 text-sm" />
                  <p className="mt-1 text-[11px] text-[#75807A]">Entre 30 minutes et 7 jours à partir de maintenant.</p>
                </div>
              )}
              {scheduledForIso && (
                <div className="text-xs text-[#059669] font-semibold">
                  ✓ Livraison programmée le {new Date(scheduledForIso).toLocaleString("fr-BE", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}
                </div>
              )}
              {!scheduledForIso && (
                <div className="text-xs text-[#D32F2F] font-semibold">
                  ⚠️ Sélectionnez {restoSlots.length > 0 ? "une date ET un créneau" : "une date et une heure"} pour continuer.
                </div>
              )}
            </div>
          )}
        </section>

        {/* Coordonnées (utilisées pour créer votre compte automatiquement au paiement) */}
        <section data-testid="checkout-contact" className="bg-white rounded-2xl border border-[#E2DFD8] p-5">
          <h2 className="font-head font-bold text-[#1A1A1A] mb-1">Vos coordonnées</h2>
          <p className="text-xs text-[#75807A] mb-4">Votre compte Nold Go sera créé automatiquement au paiement.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#3B423E] mb-1 block">Prénom *</label>
              <Input
                data-testid="checkout-firstname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Marie"
                className="h-12 rounded-xl border-[#E2DFD8] focus:border-[#059669]"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#3B423E] mb-1 block">Téléphone *</label>
              <Input
                data-testid="checkout-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+32 494 12 34 56"
                className="h-12 rounded-xl border-[#E2DFD8] focus:border-[#059669]"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-[#3B423E] mb-1 block">Email *</label>
              <Input
                data-testid="checkout-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="marie@example.com"
                className="h-12 rounded-xl border-[#E2DFD8] focus:border-[#059669]"
              />
            </div>
          </div>
        </section>

        {/* Résumé + Paiement */}
        <section data-testid="checkout-summary" className="bg-white rounded-2xl border-2 border-[#059669] p-5">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-[#3B423E]"><span>Sous-total</span><span>{subtotal.toFixed(2)} €</span></div>
            <div className="flex justify-between text-[#3B423E]">
              <span>Frais de livraison{feeQuote.computed && feeQuote.distance_km != null ? ` (${feeQuote.distance_km.toFixed(1)} km)` : ""}</span>
              <span data-testid="food-checkout-delivery-fee">
                {feeQuote.quoting ? "…" : `${activeFee.toFixed(2)} €`}
              </span>
            </div>
            <div className="flex justify-between font-head font-black text-lg pt-2 border-t border-[#E2DFD8] mt-2">
              <span>Total</span>
              <span className="text-[#059669]">{total.toFixed(2)} €</span>
            </div>
          </div>
        </section>
      </div>

      {/* Bouton Payer sticky en bas */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[#E2DFD8] p-4">
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="checkout-pay"
            onClick={pay}
            disabled={!canPay || submitting}
            className="w-full h-14 rounded-full bg-[#059669] hover:bg-[#047857] disabled:bg-[#75807A] disabled:cursor-not-allowed text-white font-bold text-base inline-flex items-center justify-center gap-2 transition-colors shadow-lg"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
            {submitting ? "Redirection Stripe…" : `Payer ${total.toFixed(2)} €`}
          </button>
          {!canPay && (
            <p className="text-center text-xs text-[#75807A] mt-2">Complétez l'adresse et vos coordonnées pour continuer.</p>
          )}
        </div>
      </div>
    </div>
  );
}
